/**
 * QAILoadingProgress Component (UI-6)
 * Progress bar with step indicators for Q AI processing
 *
 * Features:
 * - Linear progress bar with percentage
 * - Current step label
 * - Step icons/indicators
 * - Animated transitions
 * - Slow response warnings (UI-7)
 * - Partial failure warnings (UI-8)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress, Collapse, Fade } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { PROCESSING_STEPS } from '../../api/qaiApi';

// Step order for display
const STEP_ORDER = ['auth', 'user', 'balance', 'sync_status', 'signals', 'strategies', 'prompts', 'generating'];

// Step display names
const STEP_LABELS = {
    auth: 'Auth',
    user: 'User',
    balance: 'Balance',
    sync_status: 'MT5',
    signals: 'Signals',
    strategies: 'Strategies',
    prompts: 'Prompts',
    generating: 'AI'
};

// Slow response thresholds (in ms)
const SLOW_WARNING_THRESHOLD = 3000;  // 3 seconds
const VERY_SLOW_THRESHOLD = 10000;    // 10 seconds

/**
 * QAILoadingProgress Component
 * @param {object} props
 * @param {boolean} props.isLoading - Whether loading is in progress
 * @param {string} props.currentStep - Current processing step
 * @param {number} props.progress - Progress percentage (0-100)
 * @param {string} props.statusMessage - Current status message
 * @param {Array} props.warnings - Array of warning objects { code, message, recoverable }
 * @param {boolean} props.compact - Use compact display mode
 */
const QAILoadingProgress = ({
    isLoading = false,
    currentStep = null,
    progress = 0,
    statusMessage = '',
    warnings = [],
    compact = false
}) => {
    // Slow response timer state
    const [slowWarningLevel, setSlowWarningLevel] = useState(0); // 0=none, 1=slow, 2=very slow
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);

    // Start/reset slow response timer
    useEffect(() => {
        if (isLoading) {
            startTimeRef.current = Date.now();
            setSlowWarningLevel(0);

            // Check periodically
            timerRef.current = setInterval(() => {
                const elapsed = Date.now() - startTimeRef.current;
                if (elapsed >= VERY_SLOW_THRESHOLD) {
                    setSlowWarningLevel(2);
                } else if (elapsed >= SLOW_WARNING_THRESHOLD) {
                    setSlowWarningLevel(1);
                }
            }, 500);
        } else {
            // Clear timer when loading stops
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            setSlowWarningLevel(0);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isLoading]);

    // Don't render if not loading
    if (!isLoading) return null;

    // Get current step index
    const currentStepIndex = STEP_ORDER.indexOf(currentStep);

    // Compact mode - just progress bar and message
    if (compact) {
        return (
            <Fade in={isLoading}>
                <Box sx={{ width: '100%', py: 1 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 3,
                            borderRadius: 1,
                            backgroundColor: 'rgba(39, 181, 109, 0.15)',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: '#27B56D',
                                borderRadius: 1
                            }
                        }}
                    />
                    <Typography
                        sx={{
                            color: '#52525B',
                            fontSize: '0.7rem',
                            mt: 0.5,
                            textAlign: 'center'
                        }}
                    >
                        {statusMessage || 'Processing...'}
                    </Typography>
                </Box>
            </Fade>
        );
    }

    // Full display mode
    return (
        <Fade in={isLoading}>
            <Box
                sx={{
                    width: '100%',
                    p: 2,
                    background: 'rgba(39, 181, 109, 0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(39, 181, 109, 0.1)'
                }}
            >
                {/* Progress Bar */}
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ color: '#F5F5F5', fontSize: '0.8rem', fontWeight: 500 }}>
                            {statusMessage || 'Processing your request...'}
                        </Typography>
                        <Typography sx={{ color: '#52525B', fontSize: '0.75rem' }}>
                            {progress}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: 'rgba(39, 181, 109, 0.15)',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: '#27B56D',
                                borderRadius: 3,
                                transition: 'transform 0.3s ease'
                            }
                        }}
                    />
                </Box>

                {/* Step Indicators */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 0.5,
                        mb: warnings.length > 0 || slowWarningLevel > 0 ? 2 : 0
                    }}
                >
                    {STEP_ORDER.map((step, index) => {
                        const isCompleted = index < currentStepIndex;
                        const isCurrent = step === currentStep;
                        const hasWarning = warnings.some(w => w.code?.includes(step));

                        return (
                            <Box
                                key={step}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    flex: 1
                                }}
                            >
                                {/* Step Icon */}
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 0.5
                                    }}
                                >
                                    {hasWarning ? (
                                        <WarningIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                                    ) : isCompleted ? (
                                        <CheckCircleIcon sx={{ fontSize: 16, color: '#27B56D' }} />
                                    ) : isCurrent ? (
                                        <HourglassEmptyIcon
                                            sx={{
                                                fontSize: 16,
                                                color: '#27B56D',
                                                animation: 'spin 1s linear infinite',
                                                '@keyframes spin': {
                                                    '0%': { transform: 'rotate(0deg)' },
                                                    '100%': { transform: 'rotate(360deg)' }
                                                }
                                            }}
                                        />
                                    ) : (
                                        <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: '#3f3f46' }} />
                                    )}
                                </Box>
                                {/* Step Label */}
                                <Typography
                                    sx={{
                                        fontSize: '0.6rem',
                                        color: isCurrent ? '#27B56D' : isCompleted ? '#71717a' : '#3f3f46',
                                        fontWeight: isCurrent ? 600 : 400,
                                        textAlign: 'center'
                                    }}
                                >
                                    {STEP_LABELS[step]}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* Slow Response Warning (UI-7) */}
                <Collapse in={slowWarningLevel > 0}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            background: slowWarningLevel === 2 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                            borderRadius: '6px',
                            mb: warnings.length > 0 ? 1 : 0
                        }}
                    >
                        {slowWarningLevel === 2 ? (
                            <HourglassEmptyIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                        ) : (
                            <HourglassEmptyIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                        )}
                        <Typography
                            sx={{
                                fontSize: '0.75rem',
                                color: slowWarningLevel === 2 ? '#f59e0b' : '#3b82f6'
                            }}
                        >
                            {slowWarningLevel === 2
                                ? 'Thanks for your patience. This is taking longer than usual.'
                                : 'Taking a bit longer than usual...'}
                        </Typography>
                    </Box>
                </Collapse>

                {/* Partial Failure Warnings (UI-8) */}
                {warnings.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {warnings.map((warning, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 1,
                                    background: warning.recoverable ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                    borderRadius: '6px'
                                }}
                            >
                                {warning.recoverable ? (
                                    <WarningIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                                ) : (
                                    <ErrorIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                                )}
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: warning.recoverable ? '#f59e0b' : '#ef4444'
                                    }}
                                >
                                    {warning.message}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Fade>
    );
};

export default QAILoadingProgress;
